/**
 * `/admin/automation/overview` — is each ADDIS system currently on, and the one
 * control that changes the answer.
 *
 * The one question the status board cannot answer: not "did it pass last night"
 * but "if the schedule fires in the next hour, will anything happen". A paused
 * CDID turns its `admit` job red every six hours by design, so the board can be
 * red while nothing is wrong, and green while a system has been off since
 * yesterday.
 *
 * ## Three things this page is careful about
 *
 * **A verdict is never inferred here.** `running` / `paused` / `unknown` are
 * decided server-side in `addis_enablement.ts`, from the pause registry the
 * admission guard itself reads. `unknown` renders as its own state and never as
 * a neighbour: an unreadable flag is an unverified surface, not a running one.
 *
 * **A fault is not a pause.** A workflow someone disabled by hand is shown in
 * its own block with its `gh workflow enable` remedy, and it does not colour the
 * switch — because `resume` does not clear it, and a console that pretended
 * otherwise would send a reader to the wrong lever.
 *
 * **Every write is confirmed, and APD's resume is confirmed differently.**
 * Resuming APD means the next coordinator cron ships to production with no
 * further approval, which is a production decision rather than a toggle; the
 * dialog says so in those words. See `ops-pause-resume.md`.
 */
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { AddisEnablementSystem, AddisPauseVerdict } from '@a7y/types';
import { Switch, SwitchField } from '@a7y/tailwind-catalyst';
import { AlertTriangle, RefreshCw, ShieldAlert } from 'lucide-react';
import { Link } from 'react-router-dom';
import { PageHeader } from '@/components/PageHeader';
import { useConfirm } from '@/contexts/ConfirmDialogContext';
import { api } from '@/lib/api';
import { cn } from '@/lib/utils';
import { fmtLocal } from './shared';

/** A LAYOUT number — how many grey cards to show while the first read is in flight. */
const SKELETON_CARDS = 5;

const VERDICT: Record<AddisPauseVerdict, { label: string; dot: string; pill: string }> = {
  running: {
    label: 'Running',
    dot: 'bg-emerald-500',
    pill: 'border-emerald-300 bg-emerald-50 text-emerald-800 dark:border-emerald-800 dark:bg-emerald-950/50 dark:text-emerald-200',
  },
  paused: {
    label: 'Paused',
    dot: 'bg-amber-500',
    pill: 'border-amber-300 bg-amber-50 text-amber-900 dark:border-amber-700 dark:bg-amber-950/50 dark:text-amber-200',
  },
  // Amber would read as "paused, which we chose". Unknown is neither state, and
  // the runbook's words for it are "not a clean bill of health".
  unknown: {
    label: 'Unknown',
    dot: 'bg-red-500',
    pill: 'border-red-300 bg-red-50 text-red-800 dark:border-red-800 dark:bg-red-950/50 dark:text-red-200',
  },
};

function VerdictPill({ verdict }: { verdict: AddisPauseVerdict }) {
  const tone = VERDICT[verdict];
  return (
    <span
      role="status"
      className={cn(
        'inline-flex shrink-0 items-center gap-2 rounded-full border px-3 py-1 text-sm font-semibold',
        tone.pill
      )}
    >
      <span aria-hidden="true" className={cn('h-2 w-2 rounded-full', tone.dot)} />
      {tone.label}
    </span>
  );
}

/** Mono, because every one of these is a literal someone types back. */
function Mono({ children }: { children: React.ReactNode }) {
  return <code className="font-mono text-xs text-zinc-700 dark:text-zinc-300">{children}</code>;
}

/**
 * The confirm text. APD gets its own because resuming it is a production
 * decision, not a preference — the same separation `/a7y-apd-resume` makes when
 * it puts the production question through its own explicit prompt.
 */
function confirmFor(system: AddisEnablementSystem, paused: boolean) {
  if (system.key === 'apd' && !paused) {
    return {
      title: 'Resume the automated production deploy?',
      description:
        'The next coordinator cron — daily at 05:23 CEST — will ship the newest eligible immutable candidate to production with no further approval. This is a production decision, not a preference.',
      confirmLabel: 'Resume APD',
      confirmColor: 'red' as const,
    };
  }
  if (paused) {
    return {
      title: `Pause ${system.label}?`,
      description: `This stops ${system.stops}. Work already in flight still finishes and still lands. Still admitted: ${system.stillAdmitted}`,
      confirmLabel: 'Pause',
      confirmColor: 'amber' as const,
    };
  }
  return {
    title: `Resume ${system.label}?`,
    description: `${system.label} starts taking work again on its own schedule — ${system.nextRefusal}.`,
    confirmLabel: 'Resume',
    confirmColor: 'green' as const,
  };
}

function SystemCard({ system }: { system: AddisEnablementSystem }) {
  const confirm = useConfirm();
  const queryClient = useQueryClient();

  const toggle = useMutation({
    mutationFn: (paused: boolean) => api.setAddisPause(system.key, paused),
    // Refetch rather than patching the cache from the response: the board
    // carries faults and a fleet switch this write does not speak for.
    onSettled: () => queryClient.invalidateQueries({ queryKey: ['addis-enablement'] }),
  });

  const onChange = async (checked: boolean) => {
    // The switch reads "Running", so ON means running and a pause is `!checked`.
    const paused = !checked;
    if (await confirm(confirmFor(system, paused))) toggle.mutate(paused);
  };

  return (
    <section
      aria-label={system.label}
      className="rounded-lg border border-zinc-200 bg-white p-4 dark:border-zinc-700 dark:bg-zinc-900"
    >
      <div className="flex items-start justify-between gap-4">
        <div className="min-w-0">
          <h2 className="text-lg font-semibold text-zinc-900 dark:text-zinc-100">
            {system.detailPath ? (
              <Link to={system.detailPath} className="hover:underline">
                {system.label}
              </Link>
            ) : (
              system.label
            )}
          </h2>
          <p className="mt-0.5 max-w-3xl text-sm text-zinc-600 dark:text-zinc-400">
            Pausing stops {system.stops}.
          </p>
        </div>
        <div className="flex shrink-0 items-center gap-3">
          <VerdictPill verdict={system.verdict} />
          {system.togglable && (
            <SwitchField>
              <Switch
                aria-label={`${system.label} running`}
                // An `unknown` flag is not a state to toggle out of: the value
                // in the variable is one nothing accepts, so the fix is to
                // correct it, not to write over it from here.
                disabled={toggle.isPending || system.verdict === 'unknown'}
                checked={system.verdict === 'running'}
                onChange={onChange}
                color={system.verdict === 'running' ? 'emerald' : 'zinc'}
              />
            </SwitchField>
          )}
        </div>
      </div>

      {toggle.isError && (
        <p className="mt-3 rounded border border-red-300 bg-red-50 p-2 text-xs text-red-700 dark:border-red-700 dark:bg-red-950/40 dark:text-red-300">
          {(toggle.error as Error).message}
        </p>
      )}

      <dl className="mt-3 grid gap-x-6 gap-y-1 border-t border-zinc-100 pt-3 text-xs dark:border-zinc-800 sm:grid-cols-[auto_1fr]">
        <dt className="font-semibold uppercase tracking-wider text-zinc-400">Flag</dt>
        <dd className="text-zinc-600 dark:text-zinc-400">
          {system.flag ? (
            <>
              <Mono>
                {system.flag.variable}={system.flag.value}
              </Mono>{' '}
              <span className="text-zinc-400">({system.flag.source})</span>
              {system.decidedBy && system.decidedBy !== system.flag.variable && (
                <> — overridden by <Mono>{system.decidedBy}</Mono></>
              )}
            </>
          ) : (
            <>none — paused only by disabling its workflow</>
          )}
        </dd>

        <dt className="font-semibold uppercase tracking-wider text-zinc-400">Still runs</dt>
        <dd className="text-zinc-600 dark:text-zinc-400">{system.stillAdmitted}</dd>

        <dt className="font-semibold uppercase tracking-wider text-zinc-400">Cadence</dt>
        <dd className="text-zinc-600 dark:text-zinc-400">
          {system.nextRefusal}
          {system.cron && (
            <>
              {' '}
              <Mono>{system.cron}</Mono>
            </>
          )}
        </dd>

        <dt className="font-semibold uppercase tracking-wider text-zinc-400">Resume</dt>
        <dd className="text-zinc-600 dark:text-zinc-400">
          <Mono>{system.resumeScript}</Mono>
          {system.resumeCommand && <> · <Mono>{system.resumeCommand}</Mono></>}
        </dd>
      </dl>

      {/* Faults sit apart from the verdict on purpose: `resume` never clears
          one, so a reader must not be sent to the switch to fix it. */}
      {system.faults.length > 0 && (
        <div className="mt-3 rounded border border-red-300 bg-red-50 p-2 dark:border-red-800 dark:bg-red-950/40">
          <h3 className="flex items-center gap-1.5 text-[11px] font-semibold uppercase tracking-wider text-red-700 dark:text-red-300">
            <ShieldAlert className="h-3 w-3" />
            Fault — not the pause, and resume will not clear it
          </h3>
          <ul className="mt-1 space-y-0.5">
            {system.faults.map((fault) => (
              <li key={fault.workflow} className="text-xs text-red-700 dark:text-red-300">
                <Mono>{fault.workflow}</Mono> is {fault.state} — <Mono>{fault.remedy}</Mono>
              </li>
            ))}
          </ul>
        </div>
      )}
    </section>
  );
}

export default function AddisOverviewPage() {
  const enablement = useQuery({
    queryKey: ['addis-enablement'],
    queryFn: () => api.getAddisEnablement(),
    refetchInterval: 60_000,
  });

  return (
    <div className="space-y-4">
      <PageHeader
        title="ADDIS Overview"
        subtitle="Whether each automated system is currently taking work, and the switch that changes it. A pause is a repository variable the admission guard reads — every workflow stays enabled, and a paused system refuses out loud on its own cadence."
      >
        <div className="text-xs text-zinc-500">
          {enablement.data ? `Read ${fmtLocal(enablement.data.generatedAt)}` : 'Polls 60s'}
        </div>
      </PageHeader>

      {/* A failed read is never rendered as a verdict. Defaulting a card to
          "Running" because GitHub was unreachable is the worst error this page
          could make, so it shows nothing at all instead. */}
      {enablement.isError && (
        <div className="flex items-center gap-2 rounded-lg border border-red-300 bg-red-50 p-3 text-sm text-red-700 dark:border-red-700 dark:bg-red-950/40 dark:text-red-300">
          <AlertTriangle className="h-4 w-4 shrink-0" />
          Enablement could not be read, so no system's state is known. Check the Worker logs for
          /admin/autobuild/addis-enablement.
        </div>
      )}

      {/* The fleet switch is not a system and is not offered as one: it refuses
          every ingress of every purpose and stops the automated production
          deploy as a side effect, which is its own decision. */}
      {enablement.data?.fleet.paused && (
        <div className="flex items-start gap-2 rounded-lg border border-red-400 bg-red-100 p-3 text-sm text-red-950 dark:border-red-700 dark:bg-red-950/70 dark:text-red-100">
          <ShieldAlert className="mt-0.5 h-4 w-4 shrink-0" />
          <span>
            The fleet switch <Mono>{enablement.data.fleet.variable}</Mono> is on. Every system below
            is refused whatever its own flag says, including the automated production deploy.
            Clearing it is a fleet-wide decision:{' '}
            <Mono>gh variable set {enablement.data.fleet.variable} --body false</Mono>
          </span>
        </div>
      )}

      {!enablement.data && !enablement.isError && (
        <div className="space-y-4">
          {Array.from({ length: SKELETON_CARDS }).map((_, index) => (
            <div
              key={index}
              className="h-40 animate-pulse rounded-lg bg-zinc-100 dark:bg-zinc-900"
            />
          ))}
          <div className="flex items-center gap-2 text-sm text-zinc-500">
            <RefreshCw className="h-3 w-3 animate-spin" />
            Reading ADDIS enablement…
          </div>
        </div>
      )}

      {enablement.data?.systems.map((system) => <SystemCard key={system.key} system={system} />)}
    </div>
  );
}
